import { reactive, UnwrapNestedRefs, watch } from 'vue'
import { ErrorHandler } from './error'
import { isAndroid, isIOS, isIpad } from './userAgent'

interface NativeData {
  funName: string
  data: unknown | null
}

type Effect = (newVal: unknown, oldVal: unknown) => void

enum SYSTEM {
  IOS = 'ios',
  ANDROID = 'android',
  OTHERS = 'others'
}

export const system = isIpad() || isIOS() ? SYSTEM.IOS : isAndroid() ? SYSTEM.ANDROID : SYSTEM.OTHERS

export const iosReactive = new Map<string, UnwrapNestedRefs<NativeData>>()

export function createReactive(funName: string, effect: Effect) {
  let target: UnwrapNestedRefs<NativeData> | undefined = iosReactive.get(funName)
  if (!target) {
    target = reactive<NativeData>({
      funName,
      data: null
    })
    iosReactive.set(funName, target)
  }
  watch(
    () => target?.data,
    (newValue, oldValue) => effect(newValue, oldValue)
  )
}

export const getMessage = (funName: string, data: unknown) => {
  const target: UnwrapNestedRefs<NativeData> | undefined = iosReactive.get(funName)
  target && (target.data = data)
  // 赋值给reactive的data，触发watch
}

export function iosListener(funName: string) {
  return new Promise(resolve => {
    createReactive(funName, newValue => {
      resolve(newValue)
    })
  })
}

/**
 * @param funName 调用的方法名
 * @param data 字符串，数字直接传
 * @param data 对象要转成JSON字符串
 * @param data 要传多个参数，放数组里面
 */
export const appFun = async (funName: string, data?: unknown) => {
  let bridge: any = null

  try {
    if (system === SYSTEM.IOS) {
      // ios端通过messageHandlers来通知app执行某些方法，方法都是绑定在messageHandlers上
      // app通过调用window.getMessage来把数据传给h5

      if (!bridge) {
        bridge = window.webkit.messageHandlers
      }

      if (!window.getMessage) {
        window.getMessage = getMessage
      }

      if (data) {
        Array.isArray(data) ? bridge[funName].postMessage(...data) : bridge[funName].postMessage(data)
      } else {
        bridge[funName].postMessage(null)
      }

      const res = await iosListener(funName)

      return res
    } else if (system === SYSTEM.ANDROID) {
      // Android端直接调用绑定在androidJsObj上的方法来获取返回的数据
      bridge = window.androidJsObj

      return data !== null && data !== undefined
        ? (Array.isArray(data) ? bridge[funName](...data) : bridge[funName](data))
        : bridge[funName]()
    }
  } catch (err) {
    console.warn('you are not in the app')
    console.error(ErrorHandler.getErrorMessage(err))
  }
}

// 关闭webview
export const closeWebview = () => appFun('closeWin')

// 跳转个人主页
export const goToPersonalPage = (uid: number) => {
  if (!uid) return
  appFun('openPersonPage', uid)
}

// 跳转私聊
export const goToPrivateChat = (uid: number) => {
  if (!uid) return
  appFun('openPersonChat', uid)
}

// 跳转房间
export const goToRoom = (roomUid: number, nick: string) => {
  if (!roomUid) return
  appFun('openRoom', JSON.stringify({ roomUid, nick }))
  console.log('跳转房间', roomUid, nick)
}

// 举报
export const reportUser = (uid: number) => {
  if (!uid) return
  appFun('reportUser', uid)
}

// 设置黑色状态栏
export const setDarkStatusBar = () => appFun('setDarkStatusBar', true)
// 设置白色状态栏
export const setWhiteStatusBar = () => appFun('setDarkStatusBar', false)

export const setStatusBar = async (color: 'black' | 'white') => {
  if (import.meta.env.dev) return
  color === 'black' ? setDarkStatusBar() : setWhiteStatusBar()
}

// 更新用户信息
export const updateUserInfo = () => appFun('updateUserInfo')
